import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './LoanCalculator.css';
import { formatRand, quoteLoan } from '../../utils/loanPricing';

interface LoanCalculation {
  principal: number;
  interest: number;
  adminFee: number;
  vatAmount: number;
  initiationFee: number;
  totalRepayment: number;
  repaymentDateLabel: string;
  days: number;
}

interface LoanCalculatorProps {
  onCalculationChange?: (calculation: LoanCalculation) => void;
  className?: string;
}

const LoanCalculator: React.FC<LoanCalculatorProps> = ({ 
  onCalculationChange, 
  className = '' 
}) => {
  const [amount, setAmount] = useState<number>(400); // Default to middle value
  const [calculation, setCalculation] = useState<LoanCalculation | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // Hooks for navigation and authentication
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Business rules — adjust these constants to match your NCR-registered rates
  const MIN_AMOUNT = 200;
  const MAX_AMOUNT = 750;

  const calculateLoan = (loanAmount: number): LoanCalculation => {
    const quote = quoteLoan(loanAmount);
    return {
      principal: loanAmount,
      interest: quote.interestAmount,
      adminFee: quote.adminFeeExclVat,
      vatAmount: quote.vatAmount,
      initiationFee: quote.initiationFee,
      totalRepayment: quote.totalAmount,
      repaymentDateLabel: quote.repaymentDateLabel,
      days: quote.days,
    };
  };

  // Update calculation when amount changes
  useEffect(() => {
    // Only show loading for first calculation or if there's no previous calculation
    if (!calculation) {
      setIsLoading(true);
    }
    
    // Shorter delay to reduce flickering, or no delay if we already have a calculation
    const delay = calculation ? 0 : 100;
    
    const timer = setTimeout(() => {
      const newCalculation = calculateLoan(amount);
      setCalculation(newCalculation);
      setIsLoading(false);
      
      // Notify parent component
      if (onCalculationChange) {
        onCalculationChange(newCalculation);
      }
    }, delay);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount]);

  // Handle slider change
  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newAmount = parseInt(event.target.value);
    setAmount(newAmount);
  };

  // Handle apply now button
  const handleApplyNow = () => {
    if (!isAuthenticated) {
      navigate('/auth');
    } else {
      navigate(`/apply?amount=${amount}`);
    }
  };

  return (
    <div className={`loan-calculator ${className}`}>
      <div className="calculator-card">
        <div className="calculator-header">
          <h2 className="calculator-title">Quick Loan Calculator</h2>
        </div>

        <div className="amount-selector">
          <label htmlFor="loan-amount" className="amount-label">
            Loan Amount
          </label>
          
          {/* Selected Amount Display */}
          <div className="selected-amount">
            <span className="amount-display">R{amount}</span>
            <span className="amount-label-small">Selected Amount</span>
          </div>

          {/* Slider */}
          <div className="slider-container">
            <input
              type="range"
              id="loan-amount"
              className="amount-slider"
              min={MIN_AMOUNT}
              max={MAX_AMOUNT}
              step="50"
              value={amount}
              onChange={handleAmountChange}
            />
            <div className="slider-labels">
              <span>R{MIN_AMOUNT}</span>
              <span>R{MAX_AMOUNT}</span>
            </div>
          </div>
        </div>

        {/* Calculation Results — clean summary */}
        <div className="calculation-results">
          {isLoading && (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Calculating...</p>
            </div>
          )}
          {calculation && (
            <>
              <div className="result-row">
                <span className="result-label">You Get</span>
                <span className="result-value">R{calculation.principal.toFixed(2)}</span>
              </div>
              
              <div className="result-row total">
                <span className="result-label">You repay by {calculation.repaymentDateLabel}</span>
                <span className="result-value primary">{formatRand(calculation.totalRepayment)}</span>
              </div>

              <p className="calc-note">
                Interest {formatRand(calculation.interest)}, admin fee {formatRand(calculation.adminFee)}, VAT {formatRand(calculation.vatAmount)}, initiation {formatRand(calculation.initiationFee)}. Counted for {calculation.days} days left in the month.{' '}
                <a href="/faq" className="calc-faq-link">See full fee breakdown</a>
              </p>
            </>
          )}
        </div>

        {/* Call to action */}
        {calculation && (
          <div className="calculator-cta">
            <button 
              className="apply-now-btn" 
              type="button" 
              onClick={handleApplyNow}
            >
              Apply for R{amount} Now
            </button>
            
            <div className="benefits-list">
              <div className="benefit-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 12l2 2 4-4"/>
                  <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9s4.03-9 9-9c1.51 0 2.93.37 4.18 1.03"/>
                </svg>
                No hidden fees
              </div>
              <div className="benefit-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 12l2 2 4-4"/>
                  <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9s4.03-9 9-9c1.51 0 2.93.37 4.18 1.03"/>
                </svg>
                Quick 24-hour approval
              </div>
              <div className="benefit-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 12l2 2 4-4"/>
                  <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9s4.03-9 9-9c1.51 0 2.93.37 4.18 1.03"/>
                </svg>
                Student-friendly terms
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoanCalculator;
